import React, { memo, useState, useRef, useEffect } from 'react';
import { Handle, Position, NodeProps, NodeResizer } from 'reactflow';
import { NODE_CONFIG } from '../constants';
import { ComponentType, NodeData } from '../types';
import { Lock, MessageSquare, Layers, FolderOpen } from 'lucide-react';

interface CustomNodeProps extends NodeProps<NodeData> {
  connectionToolActive?: boolean;
  updateNode?: (id: string, updates: any) => void;
}

const CustomNode = (props: CustomNodeProps) => {
  const { data, selected, connectionToolActive = false, id, updateNode } = props;
  // Access config using the enum value directly (it's a string)
  const config = NODE_CONFIG[data.type as ComponentType];
  const isGroup = data.type === ComponentType.GROUP;
  const isCluster = data.isCluster && !isGroup;
  const { customPorts = [] } = data;

  // State for label editing
  const [isEditingLabel, setIsEditingLabel] = useState(false);
  const [editLabelValue, setEditLabelValue] = useState(data.label);
  const labelInputRef = useRef<HTMLInputElement>(null);

  // Update edit value when data.label changes (but not while editing)
  useEffect(() => {
    if (!isEditingLabel) {
      setEditLabelValue(data.label);
    }
  }, [data.label, isEditingLabel]);

  // Focus input when editing starts
  useEffect(() => {
    if (isEditingLabel && labelInputRef.current) {
      labelInputRef.current.focus();
      labelInputRef.current.select();
    }
  }, [isEditingLabel]);

  // Handle double click to start editing
  const handleLabelDoubleClick = (e: React.MouseEvent) => {
    if (data.locked) return; // Don't allow editing locked nodes
    e.stopPropagation();
    setIsEditingLabel(true);
    setEditLabelValue(data.label);
  };

  // Handle label save
  const handleLabelSave = () => {
    if (updateNode && editLabelValue.trim() !== data.label) {
      updateNode(id, {
        data: {
          ...data,
          label: editLabelValue.trim() || data.label,
        },
      });
    }
    setIsEditingLabel(false);
  };

  // Handle label key down
  const handleLabelKeyDown = (e: React.KeyboardEvent<HTMLInputElement>) => {
    if (e.key === 'Enter') {
      e.preventDefault();
      handleLabelSave();
    } else if (e.key === 'Escape') {
      setIsEditingLabel(false);
      setEditLabelValue(data.label);
    }
  };

  // Group Node Style
  if (isGroup) {
    return (
      <>
        <NodeResizer 
          isVisible={selected && !data.locked} 
          minWidth={200} 
          minHeight={200}
          lineStyle={{ border: '1px solid #2563eb' }}
          handleStyle={{ width: 10, height: 10, borderRadius: 0, backgroundColor: '#2563eb' }}
        />
        
        <div 
          className={`
            relative w-full h-full rounded-none transition-all duration-200 group will-change-transform
            ${selected ? 'bg-gray-50/50' : 'bg-transparent hover:scale-[1.01]'}
          `}
          style={{
            transition: 'all 0.2s cubic-bezier(0.4, 0, 0.2, 1)',
            ...(selected ? {
              animation: 'pulseBorder 2s cubic-bezier(0.4, 0, 0.6, 1) infinite',
              boxShadow: '0 0 0 0 rgba(59, 130, 246, 0.7)'
            } : {})
          }}
        >
          {/* 
             EDGE ZONE (Connection Trigger) 
             Positioned on the perimeter. We use -inset-2 to create a hit area around the border.
          */}
          <div 
            className={`
              node-edge
              absolute -inset-2 rounded-none z-10
              border-2 border-transparent
              ${connectionToolActive ? 'border-blue-300 border-dashed hover:border-blue-400 cursor-crosshair' : 'pointer-events-none'}
              transition-all duration-200
            `}
            style={{
              willChange: connectionToolActive ? 'box-shadow, border-color' : 'auto',
              ...(connectionToolActive ? {
                animation: 'pulseBorder 2s cubic-bezier(0.4, 0, 0.6, 1) infinite',
                boxShadow: '0 0 0 0 rgba(59, 130, 246, 0.7)'
              } : {})
            }}
          />

          {/* VISUAL BORDER (The dashed line) */}
          <div 
             className={`
               absolute inset-0 rounded-none border-2 border-dashed pointer-events-none z-0
               ${selected ? 'border-black' : 'border-gray-300 group-hover:border-gray-400'}
             `} 
          />
          
          {/* LABEL */}
          {isEditingLabel ? (
            <input
              ref={labelInputRef}
              type="text"
              value={editLabelValue}
              onChange={(e) => setEditLabelValue(e.target.value)}
              onBlur={handleLabelSave}
              onKeyDown={handleLabelKeyDown}
              onClick={(e) => e.stopPropagation()}
              onDoubleClick={(e) => e.stopPropagation()}
              className="absolute -top-3 left-4 bg-white px-2 text-xs font-bold text-gray-500 uppercase tracking-wider z-20 whitespace-nowrap border border-blue-500 rounded outline-none"
              style={{ minWidth: '60px' }}
            />
          ) : (
            <div 
              className="absolute -top-3 left-4 bg-white px-2 text-xs font-bold text-gray-500 uppercase tracking-wider z-20 whitespace-nowrap cursor-text hover:bg-gray-50 transition-colors"
              onDoubleClick={handleLabelDoubleClick}
              title="Double-click to rename"
            >
              {data.label}
            </div>
          )}

          {data.locked && (
             <div className="absolute top-2 right-2 text-gray-400 z-20">
               <Lock size={14} />
             </div>
          )}
          {data.comment && (
            <div className="absolute -top-1 -right-1 bg-blue-500 text-white rounded-full w-4 h-4 flex items-center justify-center z-20" title={data.comment}>
              <MessageSquare size={10} />
            </div>
          )}
          {data.nestedArchitecture && (
            <div 
              className="absolute -top-2 -right-2 bg-gradient-to-br from-purple-600 to-blue-600 text-white rounded-full w-6 h-6 flex items-center justify-center text-xs font-bold shadow-lg border-2 border-white z-20 pointer-events-none"
              title={`Contains ${data.nestedArchitecture.nodes.length} nested component${data.nestedArchitecture.nodes.length !== 1 ? 's' : ''}. Right-click and select "Open Inside" to view.`}
            >
              {data.nestedArchitecture.nodes.length}
            </div>
          )}
          
          {/* Default handles - visible when connection tool is active */}
          <Handle 
            type="target" 
            position={Position.Top} 
            style={{ 
              opacity: connectionToolActive ? 0.8 : 0,
              width: connectionToolActive ? '10px' : '0px',
              height: connectionToolActive ? '10px' : '0px',
              backgroundColor: connectionToolActive ? '#3b82f6' : 'transparent',
              border: connectionToolActive ? '2px solid white' : 'none',
              borderRadius: '50%',
              transition: 'all 0.2s'
            }} 
          />
          <Handle 
            type="target" 
            position={Position.Left} 
            style={{ 
              opacity: connectionToolActive ? 0.8 : 0,
              width: connectionToolActive ? '10px' : '0px',
              height: connectionToolActive ? '10px' : '0px',
              backgroundColor: connectionToolActive ? '#3b82f6' : 'transparent',
              border: connectionToolActive ? '2px solid white' : 'none',
              borderRadius: '50%',
              transition: 'all 0.2s'
            }} 
          />
          <Handle 
            type="source" 
            position={Position.Bottom} 
            style={{ 
              opacity: connectionToolActive ? 0.8 : 0,
              width: connectionToolActive ? '10px' : '0px',
              height: connectionToolActive ? '10px' : '0px',
              backgroundColor: connectionToolActive ? '#3b82f6' : 'transparent',
              border: connectionToolActive ? '2px solid white' : 'none',
              borderRadius: '50%',
              transition: 'all 0.2s'
            }} 
          />
          <Handle 
            type="source" 
            position={Position.Right} 
            style={{ 
              opacity: connectionToolActive ? 0.8 : 0,
              width: connectionToolActive ? '10px' : '0px',
              height: connectionToolActive ? '10px' : '0px',
              backgroundColor: connectionToolActive ? '#3b82f6' : 'transparent',
              border: connectionToolActive ? '2px solid white' : 'none',
              borderRadius: '50%',
              transition: 'all 0.2s'
            }} 
          />
          
          {/* Dynamic Ports for Group */}
          {customPorts.map((port) => {
            let style: React.CSSProperties = {
              position: 'absolute',
              zIndex: 50,
              width: '8px',
              height: '8px',
              backgroundColor: '#fff',
              border: '1px solid #000',
              borderRadius: '50%',
            };

            let position = Position.Top;
            if (port.side === 'top') {
               style.left = `${port.percentage}%`;
               style.top = '-4px';
               position = Position.Top;
            } else if (port.side === 'right') {
               style.left = '100%';
               style.top = `${port.percentage}%`;
               style.transform = 'translate(-50%, 0)';
               position = Position.Right;
            } else if (port.side === 'bottom') {
               style.left = `${port.percentage}%`;
               style.top = '100%';
               style.transform = 'translate(0, -50%)';
               position = Position.Bottom;
            } else if (port.side === 'left') {
               style.left = '-4px';
               style.top = `${port.percentage}%`;
               position = Position.Left;
            }

            return (
              <Handle 
                key={port.id} 
                id={port.id} 
                type={port.handleType ?? 'source'} 
                position={position} 
                style={style} 
              />
            );
          })}
        </div>
      </>
    );
  }

  // Cluster Mode (regular component rendered as container with stacked effect)
  if (isCluster) {
    // Get dimensions from node props or defaults - ensure minimum size for visibility
    const propsAny = props as any;
    const nodeWidthValue = propsAny.width;
    const nodeHeightValue = propsAny.height;
    const nodeStyle = propsAny.style;
    const nodeWidth = typeof nodeWidthValue === 'number' ? nodeWidthValue : (typeof nodeStyle?.width === 'number' ? nodeStyle.width : parseInt(String(nodeWidthValue || nodeStyle?.width || 130), 10));
    const nodeHeight = typeof nodeHeightValue === 'number' ? nodeHeightValue : (typeof nodeStyle?.height === 'number' ? nodeStyle.height : parseInt(String(nodeHeightValue || nodeStyle?.height || 40), 10));
    const clusterWidth = Math.max(nodeWidth, 130);
    const clusterHeight = Math.max(nodeHeight, 40);
    
    // Determine if label should be outside (for larger clusters)
    const isLargeCluster = clusterWidth > 200 || clusterHeight > 60;
    
    return (
      <>
        <NodeResizer 
          isVisible={selected && !data.locked} 
          minWidth={130} 
          minHeight={40}
          lineStyle={{ border: '1px solid #2563eb' }}
          handleStyle={{ width: 10, height: 10, borderRadius: 0, backgroundColor: '#2563eb' }}
        />
        
        <div 
          className={`
            relative transition-all duration-300 group will-change-transform
            ${selected ? 'bg-blue-50/30' : 'bg-transparent'}
          `}
          style={{ 
            width: clusterWidth,
            height: clusterHeight,
            minWidth: 130,
            minHeight: 40,
            overflow: 'visible',
            transition: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
            transform: 'translateY(0) scale(1)',
            ...(selected ? {
              animation: 'pulseBorder 2s cubic-bezier(0.4, 0, 0.6, 1) infinite',
              boxShadow: '0 2px 4px rgba(0,0,0,0.08), inset 0 1px 2px rgba(0,0,0,0.05), 0 0 0 2px rgba(37, 99, 235, 0.2), 0 0 0 0 rgba(59, 130, 246, 0.7)'
            } : {})
          }}
          onMouseEnter={(e) => {
            if (!selected) {
              e.currentTarget.style.transform = 'scale(1.02) translateY(-1px)';
              e.currentTarget.style.boxShadow = '0 8px 16px rgba(0,0,0,0.15), 0 0 0 1px rgba(59, 130, 246, 0.1)';
            }
          }}
          onMouseLeave={(e) => {
            e.currentTarget.style.transform = selected ? 'scale(1) translateY(0)' : 'translateY(0) scale(1)';
            e.currentTarget.style.boxShadow = '';
          }}
        >
          {/* EDGE ZONE */}
          <div 
            className={`
              node-edge
              absolute -inset-2 z-10
              border-2 bg-transparent
              ${connectionToolActive ? 'border-blue-300 border-dashed' : 'border-transparent pointer-events-none'}
              ${connectionToolActive ? 'hover:border-blue-400 cursor-crosshair' : ''}
              transition-all duration-200
            `}
            style={{
              willChange: connectionToolActive ? 'box-shadow, border-color' : 'auto',
              background: connectionToolActive 
                ? 'linear-gradient(135deg, rgba(59, 130, 246, 0.1) 0%, rgba(37, 99, 235, 0.1) 100%)'
                : 'transparent',
              boxShadow: connectionToolActive
                ? '0 0 8px rgba(59, 130, 246, 0.3), inset 0 0 8px rgba(59, 130, 246, 0.1)'
                : 'none',
              ...(connectionToolActive ? {
                animation: 'pulseBorder 2s cubic-bezier(0.4, 0, 0.6, 1) infinite',
              } : {})
            }}
            onMouseEnter={(e) => {
              if (!connectionToolActive) {
                e.currentTarget.style.boxShadow = '0 0 12px rgba(59, 130, 246, 0.4)';
                e.currentTarget.style.borderColor = 'rgba(59, 130, 246, 0.6)';
              }
            }}
            onMouseLeave={(e) => {
              if (!connectionToolActive) {
                e.currentTarget.style.boxShadow = '';
                e.currentTarget.style.borderColor = 'transparent';
              }
            }}
          />

          {/* Main container */}
          <div 
            className={`
              absolute inset-0 pointer-events-none bg-white
              transition-all duration-200
            `}
            style={{ 
              zIndex: 3,
              border: '2px solid #9ca3af',
              boxShadow: selected 
                ? '0 2px 4px rgba(0,0,0,0.08), inset 0 1px 2px rgba(0,0,0,0.05), 0 0 0 2px rgba(37, 99, 235, 0.2)'
                : '0 2px 4px rgba(0,0,0,0.08), inset 0 1px 2px rgba(0,0,0,0.05)',
            }}
          />
          
          {/* CLUSTER BADGE - Visual indicator */}
          <div 
            className="absolute -top-2 -right-2 text-white rounded-full z-30 pointer-events-none"
            style={{
              padding: '6px',
              background: 'linear-gradient(135deg, #3b82f6 0%, #2563eb 100%)',
              border: '2px solid white',
              boxShadow: '0 2px 4px rgba(0,0,0,0.2), 0 0 0 1px rgba(0,0,0,0.1)',
            }}
          >
            <Layers size={12} />
            {data.instanceCount && data.instanceCount > 1 && (
              <div 
                className="absolute -bottom-1 -right-1 bg-white text-blue-600 rounded-full text-[8px] font-bold w-4 h-4 flex items-center justify-center border border-blue-200"
                style={{ fontSize: '8px' }}
              >
                {data.instanceCount}
              </div>
            )}
          </div>
          
          {/* LABEL with component info - adapts to cluster size */}
          {isLargeCluster ? (
            // External label for larger clusters
            isEditingLabel ? (
              <input
                ref={labelInputRef}
                type="text"
                value={editLabelValue}
                onChange={(e) => setEditLabelValue(e.target.value)}
                onBlur={handleLabelSave}
                onKeyDown={handleLabelKeyDown}
                onClick={(e) => e.stopPropagation()}
                onDoubleClick={(e) => e.stopPropagation()}
                className="absolute -top-4 left-2 bg-white px-2 py-0.5 rounded text-[10px] font-bold text-gray-700 uppercase tracking-wider z-30 whitespace-nowrap shadow-sm border-2 border-blue-500 outline-none"
                style={{ minWidth: '60px' }}
              />
            ) : (
              <div 
                className="absolute -top-4 left-2 bg-white px-2 py-0.5 rounded text-[10px] font-bold text-gray-700 uppercase tracking-wider z-30 whitespace-nowrap shadow-sm border border-gray-200 cursor-text hover:bg-gray-50 transition-colors"
                onDoubleClick={handleLabelDoubleClick}
                title="Double-click to rename"
              >
                {data.label}
                {data.instanceCount && data.instanceCount > 1 && (
                  <span className="ml-1 text-blue-600">({data.instanceCount}x)</span>
                )}
              </div>
            )
          ) : (
            // Internal label for smaller clusters - centered properly
            <div 
              className="absolute inset-0 z-20 flex items-center justify-center gap-1"
              style={{
                paddingLeft: '8px',
                paddingRight: '8px',
              }}
            >
              <div className="flex items-center gap-1">
                <div className="text-black [&>svg]:w-3 [&>svg]:h-3 flex-shrink-0 pointer-events-none">
                  {config.icon}
                </div>
                {isEditingLabel ? (
                  <input
                    ref={labelInputRef}
                    type="text"
                    value={editLabelValue}
                    onChange={(e) => setEditLabelValue(e.target.value)}
                    onBlur={handleLabelSave}
                    onKeyDown={handleLabelKeyDown}
                    onClick={(e) => e.stopPropagation()}
                    onDoubleClick={(e) => e.stopPropagation()}
                    className="text-[10px] font-bold text-black leading-tight bg-white border-2 border-blue-500 rounded px-1 outline-none"
                    style={{ minWidth: '40px', maxWidth: '100px' }}
                  />
                ) : (
                  <div className="leading-none">
                    <div 
                      className="text-[10px] font-bold text-black leading-tight cursor-text hover:bg-gray-100 px-1 rounded transition-colors"
                      onDoubleClick={handleLabelDoubleClick}
                      title="Double-click to rename"
                    >
                      {data.label}
                    </div>
                    <div className="text-[7px] text-gray-500 uppercase tracking-wider font-medium leading-tight pointer-events-none">
                      {data.type}
                      {data.instanceCount && data.instanceCount > 1 && ` • ${data.instanceCount}x`}
                    </div>
                  </div>
                )}
              </div>
            </div>
          )}

          {data.locked && (
             <div className="absolute top-2 right-2 text-gray-400 z-20">
               <Lock size={14} />
             </div>
          )}
          {data.comment && (
            <div className="absolute -top-1 -right-1 bg-blue-500 text-white rounded-full w-4 h-4 flex items-center justify-center z-20" title={data.comment}>
              <MessageSquare size={10} />
            </div>
          )}
          {data.nestedArchitecture && (
            <div 
              className="absolute -top-2 -right-2 bg-gradient-to-br from-purple-600 to-blue-600 text-white rounded-full w-6 h-6 flex items-center justify-center text-xs font-bold shadow-lg border-2 border-white z-20 pointer-events-none"
              title={`Contains ${data.nestedArchitecture.nodes.length} nested component${data.nestedArchitecture.nodes.length !== 1 ? 's' : ''}. Right-click and select "Open Inside" to view.`}
            >
              {data.nestedArchitecture.nodes.length}
            </div>
          )}
          
          {/* Default handles - visible when connection tool is active */}
          <Handle 
            type="target" 
            position={Position.Top} 
            style={{ 
              opacity: connectionToolActive ? 0.8 : 0,
              width: connectionToolActive ? '10px' : '0px',
              height: connectionToolActive ? '10px' : '0px',
              backgroundColor: connectionToolActive ? '#3b82f6' : 'transparent',
              border: connectionToolActive ? '2px solid white' : 'none',
              borderRadius: '50%',
              transition: 'all 0.2s'
            }} 
          />
          <Handle 
            type="target" 
            position={Position.Left} 
            style={{ 
              opacity: connectionToolActive ? 0.8 : 0,
              width: connectionToolActive ? '10px' : '0px',
              height: connectionToolActive ? '10px' : '0px',
              backgroundColor: connectionToolActive ? '#3b82f6' : 'transparent',
              border: connectionToolActive ? '2px solid white' : 'none',
              borderRadius: '50%',
              transition: 'all 0.2s'
            }} 
          />
          <Handle 
            type="source" 
            position={Position.Bottom} 
            style={{ 
              opacity: connectionToolActive ? 0.8 : 0,
              width: connectionToolActive ? '10px' : '0px',
              height: connectionToolActive ? '10px' : '0px',
              backgroundColor: connectionToolActive ? '#3b82f6' : 'transparent',
              border: connectionToolActive ? '2px solid white' : 'none',
              borderRadius: '50%',
              transition: 'all 0.2s'
            }} 
          />
          <Handle 
            type="source" 
            position={Position.Right} 
            style={{ 
              opacity: connectionToolActive ? 0.8 : 0,
              width: connectionToolActive ? '10px' : '0px',
              height: connectionToolActive ? '10px' : '0px',
              backgroundColor: connectionToolActive ? '#3b82f6' : 'transparent',
              border: connectionToolActive ? '2px solid white' : 'none',
              borderRadius: '50%',
              transition: 'all 0.2s'
            }} 
          />

          {/* Dynamic Ports for Cluster */}
          {customPorts.map((port) => {
            let style: React.CSSProperties = {
              position: 'absolute',
              zIndex: 100,
              width: '10px',
              height: '10px',
              backgroundColor: '#fff',
              border: '2px solid #000',
              borderRadius: '50%',
            };

            let position = Position.Top;
            if (port.side === 'top') {
               style.left = `${port.percentage}%`;
               style.top = '-4px';
               position = Position.Top;
            } else if (port.side === 'right') {
               style.left = '100%';
               style.top = `${port.percentage}%`;
               style.transform = 'translate(-50%, 0)';
               position = Position.Right;
            } else if (port.side === 'bottom') {
               style.left = `${port.percentage}%`;
               style.top = '100%';
               style.transform = 'translate(0, -50%)';
               position = Position.Bottom;
            } else if (port.side === 'left') {
               style.left = '-4px';
               style.top = `${port.percentage}%`;
               position = Position.Left;
            }

            return (
              <Handle 
                key={port.id} 
                id={port.id} 
                type={port.handleType ?? 'source'} 
                position={position} 
                style={style} 
              />
            );
          })}
        </div>
      </>
    );
  }

  // Standard Node Style
  return (
    <div className="relative group">
       {/* 
          EDGE ZONE (Outer Rim) 
          - Large hit area for adding connections
       */}
       <div 
         className={`
           node-edge
           absolute -inset-4 rounded-none transition-all duration-200 z-0
           border-2 border-transparent
           ${connectionToolActive ? 'border-blue-300 border-dashed hover:border-blue-500 hover:bg-blue-50/10 cursor-crosshair' : 'pointer-events-none'}
         `}
         style={{
           width: '80px',
           height: '30px',
           left: 0,
           top: 0,
           willChange: connectionToolActive ? 'box-shadow, border-color' : 'auto',
           ...(connectionToolActive ? {
             animation: 'pulseBorder 2s cubic-bezier(0.4, 0, 0.6, 1) infinite',
             boxShadow: '0 0 0 0 rgba(59, 130, 246, 0.7)'
           } : {})
         }}
       />

       {/* BODY ZONE */}
       <div 
        className={`
          node-body
          relative z-10 px-2 py-1 rounded-none min-w-[60px]
          transition-all duration-200 bg-white will-change-transform
          ${selected 
            ? 'border-2 border-black shadow-[2px_2px_0px_0px_rgba(0,0,0,1)]' 
            : 'border border-black shadow-[1px_1px_0px_0px_rgba(0,0,0,0.1)] hover:shadow-[2px_2px_0px_0px_rgba(0,0,0,0.15)] hover:scale-[1.02]'
          }
          ${data.locked ? 'opacity-90 bg-gray-50' : ''}
        `}
        style={{
          transform: selected ? 'scale(1)' : undefined,
          transition: 'all 0.2s cubic-bezier(0.4, 0, 0.2, 1)',
          ...(selected ? {
            animation: 'pulseBorder 2s cubic-bezier(0.4, 0, 0.6, 1) infinite',
            boxShadow: '2px 2px 0px 0px rgba(0,0,0,1), 0 0 0 0 rgba(59, 130, 246, 0.7)'
          } : {})
        }}
      >
        <div className="flex items-center gap-1">
          <div className="text-black [&>svg]:w-3 [&>svg]:h-3 pointer-events-none">
            {config.icon}
          </div>
          <div className="flex-1 min-w-0">
            {isEditingLabel ? (
              <input
                ref={labelInputRef}
                type="text"
                value={editLabelValue}
                onChange={(e) => setEditLabelValue(e.target.value)}
                onBlur={handleLabelSave}
                onKeyDown={handleLabelKeyDown}
                onClick={(e) => e.stopPropagation()}
                onDoubleClick={(e) => e.stopPropagation()}
                className="text-[10px] font-bold text-black leading-tight bg-white border-2 border-blue-500 rounded px-1 outline-none w-full"
                style={{ minWidth: '40px' }}
              />
            ) : (
              <div 
                className="text-[10px] font-bold text-black leading-tight cursor-text hover:bg-gray-100 px-1 rounded transition-colors"
                onDoubleClick={handleLabelDoubleClick}
                title="Double-click to rename"
              >
                {data.label}
              </div>
            )}
            <div className="text-[7px] text-gray-500 uppercase tracking-wider font-medium pointer-events-none">{data.type}</div>
          </div>
          {data.locked && (
            <div className="ml-auto text-gray-400 pointer-events-none">
              <Lock size={8} />
            </div>
          )}
          {data.comment && (
            <div className="ml-auto text-blue-500 pointer-events-none" title={data.comment}>
              <MessageSquare size={8} />
            </div>
          )}
          {data.nestedArchitecture && (
            <div 
              className="ml-auto bg-gradient-to-br from-purple-600 to-blue-600 text-white rounded-full w-5 h-5 flex items-center justify-center text-[9px] font-bold shadow-md border border-white pointer-events-none"
              title={`Contains ${data.nestedArchitecture.nodes.length} nested component${data.nestedArchitecture.nodes.length !== 1 ? 's' : ''}. Right-click and select "Open Inside" to view.`}
            >
              {data.nestedArchitecture.nodes.length}
            </div>
          )}
        </div>
      </div>

      {/* Default handles - visible when connection tool is active */}
      <Handle 
        type="target" 
        position={Position.Top} 
        style={{ 
          opacity: connectionToolActive ? 0.8 : 0,
          width: connectionToolActive ? '10px' : '0px',
          height: connectionToolActive ? '10px' : '0px',
          backgroundColor: connectionToolActive ? '#3b82f6' : 'transparent',
          border: connectionToolActive ? '2px solid white' : 'none',
          borderRadius: '50%',
          transition: 'all 0.2s'
        }} 
      />
      <Handle 
        type="target" 
        position={Position.Left} 
        style={{ 
          opacity: connectionToolActive ? 0.8 : 0,
          width: connectionToolActive ? '10px' : '0px',
          height: connectionToolActive ? '10px' : '0px',
          backgroundColor: connectionToolActive ? '#3b82f6' : 'transparent',
          border: connectionToolActive ? '2px solid white' : 'none',
          borderRadius: '50%',
          transition: 'all 0.2s'
        }} 
      />
      <Handle 
        type="source" 
        position={Position.Bottom} 
        style={{ 
          opacity: connectionToolActive ? 0.8 : 0,
          width: connectionToolActive ? '10px' : '0px',
          height: connectionToolActive ? '10px' : '0px',
          backgroundColor: connectionToolActive ? '#3b82f6' : 'transparent',
          border: connectionToolActive ? '2px solid white' : 'none',
          borderRadius: '50%',
          transition: 'all 0.2s'
        }} 
      />
      <Handle 
        type="source" 
        position={Position.Right} 
        style={{ 
          opacity: connectionToolActive ? 0.8 : 0,
          width: connectionToolActive ? '10px' : '0px',
          height: connectionToolActive ? '10px' : '0px',
          backgroundColor: connectionToolActive ? '#3b82f6' : 'transparent',
          border: connectionToolActive ? '2px solid white' : 'none',
          borderRadius: '50%',
          transition: 'all 0.2s'
        }} 
      />

      {/* Dynamic Custom Ports */}
      {customPorts.map((port) => {
          let style: React.CSSProperties = {
            position: 'absolute',
            zIndex: 50,
            width: '8px',
            height: '8px',
            backgroundColor: '#fff',
            border: '1px solid #000',
            borderRadius: '50%',
          };

          let position = Position.Top;
          if (port.side === 'top') {
             style.left = `${port.percentage}%`;
             style.top = '-4px'; // Half inside, half out roughly, or just on border
             position = Position.Top;
          } else if (port.side === 'right') {
             style.left = 'calc(100% - 4px)'; // Adjust to align with border
             style.top = `${port.percentage}%`;
             position = Position.Right;
          } else if (port.side === 'bottom') {
             style.left = `${port.percentage}%`;
             style.top = 'calc(100% - 4px)';
             position = Position.Bottom;
          } else if (port.side === 'left') {
             style.left = '-4px';
             style.top = `${port.percentage}%`;
             position = Position.Left;
          }

          return (
            <Handle 
              key={port.id} 
              id={port.id} 
              type={port.handleType ?? 'source'} 
              position={position} 
              style={style} 
            />
          );
      })}
    </div>
  );
};

export default memo(CustomNode);